import { useQuery, useMutation } from '@tanstack/react-query';
import {
  getProjectReport,
  getAttendanceReport,
  downloadAttendanceReportXlsx,
  getPayrollReport,
  getPayrollDailyReport,
  downloadPayrollDailyReportXlsx,
  getPayrollProjectsReport,
  downloadPayrollProjectsReportXlsx,
  getDetailedProjectReport,
  downloadDetailedProjectReportXlsx,
  getInvoicingStatusReport,
  getBillabilityReport,
  getBillabilityUserTrend,
  type ReportFilters,
  type AttendanceReportFilters,
  type DetailedReportFilters,
} from '../services/api/reports';

export function useProjectReport(filters: ReportFilters | null) {
  return useQuery({
    queryKey: ['report', 'project', filters],
    queryFn: () => getProjectReport(filters!),
    enabled: !!filters?.from && !!filters?.to,
  });
}

export function useAttendanceReport(filters: AttendanceReportFilters | null) {
  return useQuery({
    queryKey: ['report', 'attendance', filters],
    queryFn: () => getAttendanceReport(filters!),
    enabled: !!filters?.from && !!filters?.to,
  });
}

/** MKS-316 — the attendance grid + summary as a workbook. */
export function useDownloadAttendanceReportXlsx() {
  return useMutation({
    mutationFn: (filters: AttendanceReportFilters) => downloadAttendanceReportXlsx(filters),
    onSuccess: (blob, filters) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_${filters.from}_${filters.to}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });
}

export function usePayrollReport(filters: ReportFilters | null) {
  return useQuery({
    queryKey: ['report', 'payroll', filters],
    queryFn: () => getPayrollReport(filters!),
    enabled: !!filters?.from && !!filters?.to,
  });
}

export function useBillabilityReport(filters: ReportFilters | null) {
  return useQuery({
    queryKey: ['report', 'billability', filters],
    queryFn: () => getBillabilityReport(filters!),
    enabled: !!filters?.from && !!filters?.to,
  });
}

/**
 * Quick task 260602-g6r — Trailing-12-month margin1 + commitCount series for
 * a single user. Backs the hover-trendline popover on the Billability Report
 * username cell. `enabled` is the gate that prevents the fetch until the
 * popover opens; `staleTime` of 5 minutes avoids refetches inside the same
 * session as the cursor moves between rows.
 */
export function useBillabilityUserTrend(userId: number | null, enabled: boolean) {
  return useQuery({
    queryKey: ['report', 'billability', 'user-trend', userId],
    queryFn: () => getBillabilityUserTrend(userId!),
    enabled: enabled && userId != null,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePayrollDailyReport(filters: ReportFilters | null) {
  return useQuery({
    queryKey: ['report', 'payroll', 'daily', filters],
    queryFn: () => getPayrollDailyReport(filters!),
    enabled: !!filters?.from && !!filters?.to,
  });
}

export function useDownloadPayrollDailyReportXlsx() {
  return useMutation({
    mutationFn: (filters: ReportFilters) => downloadPayrollDailyReportXlsx(filters),
    onSuccess: (blob, filters) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_days_${filters.from}_${filters.to}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });
}

export function usePayrollProjectsReport(filters: ReportFilters | null) {
  return useQuery({
    queryKey: ['report', 'payroll', 'projects', filters],
    queryFn: () => getPayrollProjectsReport(filters!),
    enabled: !!filters?.from && !!filters?.to,
  });
}

export function useDownloadPayrollProjectsReportXlsx() {
  return useMutation({
    mutationFn: (filters: ReportFilters) => downloadPayrollProjectsReportXlsx(filters),
    onSuccess: (blob, filters) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_projects_${filters.from}_${filters.to}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });
}

export function useInvoicingStatusReport(filters: ReportFilters | null) {
  return useQuery({
    queryKey: ['report', 'invoicing-status', filters],
    queryFn: () => getInvoicingStatusReport(filters!),
    enabled: !!filters?.from && !!filters?.to,
  });
}

export function useDetailedProjectReport(filters: DetailedReportFilters | null) {
  return useQuery({
    queryKey: ['report', 'project', 'detailed', filters],
    queryFn: () => getDetailedProjectReport(filters!),
    enabled: !!filters?.from && !!filters?.to && !!filters?.projectIds?.length,
  });
}

export function useDownloadDetailedReportXlsx() {
  return useMutation({
    mutationFn: (vars: { filters: DetailedReportFilters; projectNames?: string[] }) =>
      downloadDetailedProjectReportXlsx(vars.filters),
    onSuccess: ({ blob, filename }, { filters, projectNames }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const label = projectNames?.length
        ? projectNames.join('_').replace(/[^a-zA-Z0-9_\-. ]/g, '')
        : null;
      a.download = filename || (label
        ? `${label}_report_${filters.from}_${filters.to}.xlsx`
        : `project_report_${filters.from}_${filters.to}.xlsx`);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });
}
